import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { premiumApi } from "../../services/premiumApi";
import { useSocket } from "../../hooks/useSocket";

// Import badge images
import arcaneBadge from "../../assets/arcanebadge.png";
import bronzeBadge from "../../assets/bronzebadge.png";
import goldBadge from "../../assets/goldbadge.png";

// Initialize Stripe with publishable key from environment variables
// (Stripe is not used in this component)

// PayPal options with sandbox client ID for testing
const paypalClientId =
  import.meta.env.VITE_PAYPAL_CLIENT_ID ||
  "sb-hjlks28425799@business.example.com";

// Debug PayPal configuration
console.log("PayPal Client ID:", paypalClientId);

const paypalOptions = {
  "client-id": import.meta.env.VITE_PAYPAL_CLIENT_ID,
  currency: "USD",
  intent: "capture",
  components: "buttons",
  mode: import.meta.env.VITE_PAYPAL_MODE || "sandbox",
};

const PremiumModal = ({ isOpen, onClose }) => {
  const [selectedPlan, setSelectedPlan] = useState("30min");
  const [paymentMethod, setPaymentMethod] = useState("card");

  // Map plans to their respective badge images
  const plans = {
    "30min": {
      name: "30 Minutes Boost",
      tier: "Bronze",
      price: 5.0,
      badge: bronzeBadge,
      features: [
        "Get priority matching",
        "Match with users that match your preferences",
        "Ad-free experience",
        "Enhanced matching algorithm",
        "Premium chat features",
      ],
    },
    "1hour": {
      name: "1 Hour Boost",
      tier: "Gold",
      price: 8.99,
      badge: goldBadge,
      features: [
        "All 30min features",
        "Double the boosted time",
        "Extended preference settings",
      ],
    },
    "24hour": {
      name: "24 Hours Boost",
      tier: "Arcane",
      price: 14.99,
      badge: arcaneBadge,
      features: [
        "All 1 hour features",
        "Whole day of premium access",
        "Save your preferences for future sessions",
      ],
    },
  };

  const { socket } = useSocket();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isPremiumActive, setIsPremiumActive] = useState(false);

  // Check if premium is already active
  useEffect(() => {
    const premiumStatus = premiumApi.getPremiumStatus();
    if (premiumStatus.isActive) {
      setIsPremiumActive(true);
    }
  }, []);

  // State for card payment
  const [cardElement] = useState(null);

  // Initialize Stripe elements when component mounts
  useEffect(() => {
    if (paymentMethod === "card" && !cardElement) {
      // In a real implementation, we would initialize Stripe Elements here
      // const stripe = await stripePromise;
      // const elements = stripe.elements();
      // const cardElement = elements.create('card');
      // cardElement.mount('#card-element');
      // setCardElement(cardElement);
    }
  }, [paymentMethod, cardElement]);

  const handlePaymentSubmit = async () => {
    try {
      setIsProcessing(true);
      setPaymentError(null);

      // In a real app, this would connect to Stripe or PayPal APIs
      // For demo purposes, we'll simulate a successful payment
      console.log(
        `Processing ${plans[selectedPlan].price} payment for ${plans[selectedPlan].name}`
      );

      // Simulate payment processing with our API
      const paymentData = {
        planId: selectedPlan,
        paymentMethod: "card",
        amount: plans[selectedPlan].price,
        // In real implementation, this would be a token from Stripe
        paymentToken: `tok_${Math.random().toString(36).substring(2, 15)}`,
      };

      // Process payment through our backend API
      const response = await premiumApi.processPremium(paymentData);

      if (!response || !response.success) {
        throw new Error("Payment could not be processed");
      }

      // Store premium status in localStorage
      const premiumData = premiumApi.storePremiumStatus({
        planId: selectedPlan,
        features: plans[selectedPlan].features,
      });

      // Notify server about premium status if socket is connected
      if (socket) {
        socket.emit("setPremiumStatus", {
          isPremium: true,
          expiryDate: premiumData.expiry,
        });
      }

      setIsPremiumActive(true);
      setIsProcessing(false);

      // Close modal after a short delay to show success state
      setTimeout(() => {
        onClose();
        // Refresh the page to update UI
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Payment processing error:", error);
      setPaymentError(
        "There was an error processing your payment. Please try again."
      );
      setIsProcessing(false);
    }
  };

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalVariants = {
    hidden: { scale: 0.8, opacity: 0, y: 50 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    exit: {
      scale: 0.8,
      opacity: 0,
      y: 50,
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-xl p-2 sm:p-4"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={backdropVariants}
          onClick={onClose}
        >
          <motion.div
            className="relative bg-[#0d0721]/80 backdrop-blur-2xl w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg rounded-2xl p-4 sm:p-6 overflow-hidden text-white border border-white/20 shadow-2xl shadow-purple-500/20 z-[105] max-h-[95vh] overflow-y-auto"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Glassmorphism overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-black/10 rounded-2xl pointer-events-none"></div>

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 z-[110] flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-gray-800/80 rounded-full text-gray-300 hover:text-white hover:bg-gray-700 transition-all"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 sm:h-5 sm:w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Header */}
            <div className="text-center mb-4 sm:mb-6 pt-2">
              <div className="flex justify-center mb-2">
                <div className="text-3xl sm:text-4xl md:text-5xl">🐼</div>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white via-purple-200 to-fuchsia-200 text-transparent bg-clip-text">
                Voodo Premium
              </h2>
              <p className="text-purple-300 mt-1 sm:mt-2 text-sm sm:text-base">
                Enhance your experience with premium
              </p>
            </div>

            {/* Plan selection with badges - Responsive grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-2 mb-4 sm:mb-6">
              {Object.keys(plans).map((planId) => (
                <motion.button
                  key={planId}
                  onClick={() => setSelectedPlan(planId)}
                  className={`relative p-3 sm:p-3 rounded-lg border ${
                    selectedPlan === planId
                      ? "border-purple-400 bg-purple-900/40"
                      : "border-gray-700 bg-black/40 hover:border-gray-500"
                  } transition-all duration-200 flex flex-col items-center justify-center min-h-[100px] sm:min-h-[120px]`}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Badge Image */}
                  <img
                    src={plans[planId].badge}
                    alt={`${plans[planId].name} badge`}
                    className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mb-1 sm:mb-2 object-contain"
                  />
                  {/* Tier Label */}
                  <div
                    className={`text-xs font-bold mb-1 px-2 py-0.5 rounded-full ${
                      planId === "30min"
                        ? "bg-amber-600/20 text-amber-300 border border-amber-500/30"
                        : planId === "1hour"
                        ? "bg-yellow-500/20 text-yellow-300 border border-yellow-400/30"
                        : "bg-purple-600/20 text-purple-300 border border-purple-500/30"
                    }`}
                  >
                    {plans[planId].tier}
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-center leading-tight">
                    {plans[planId].name}
                  </p>
                  <p className="text-sm sm:text-lg font-bold">
                    ${plans[planId].price.toFixed(2)}
                  </p>
                  {selectedPlan === planId && (
                    <motion.div
                      className="absolute inset-0 border-2 border-purple-500 rounded-lg"
                      layoutId="selectedPlan"
                      initial={false}
                    />
                  )}
                </motion.button>
              ))}
            </div>

            {/* Features */}
            <div className="mb-4 sm:mb-6 bg-black/20 backdrop-blur-sm p-3 sm:p-4 rounded-lg border border-white/10">
              <h3 className="text-base sm:text-lg font-semibold mb-2">
                What you'll get:
              </h3>
              <ul className="space-y-1 sm:space-y-2">
                {plans[selectedPlan].features.map((feature, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <svg
                      className="w-4 h-4 text-purple-400 mr-2 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-xs sm:text-sm leading-relaxed">
                      {feature}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Payment method selection */}
            <div className="mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm text-gray-300 mb-2">
                Select payment method:
              </p>
              <div className="flex space-x-2">
                <button
                  className={`flex-1 py-2 px-3 rounded-md transition-all text-sm sm:text-base ${
                    paymentMethod === "card"
                      ? "bg-purple-600 text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  }`}
                  onClick={() => setPaymentMethod("card")}
                >
                  Credit Card
                </button>
                <button
                  className={`flex-1 py-2 px-3 rounded-md transition-all text-sm sm:text-base ${
                    paymentMethod === "paypal"
                      ? "bg-[#0070ba] text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  }`}
                  onClick={() => setPaymentMethod("paypal")}
                >
                  PayPal
                </button>
              </div>
            </div>

            {/* Payment form or button */}
            {isPremiumActive ? (
              <div className="text-center py-3">
                <div className="bg-green-500/20 backdrop-blur-sm text-green-300 py-3 px-4 rounded-md mb-4 border border-green-500/20">
                  <svg
                    className="w-6 h-6 mx-auto mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <p className="text-sm sm:text-base">
                    You already have an active premium plan!
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="w-full py-3 rounded-md bg-gradient-to-r from-purple-600 to-fuchsia-500 hover:from-purple-700 hover:to-fuchsia-600 text-white font-medium transition-all duration-300 text-sm sm:text-base"
                >
                  Continue
                </button>
              </div>
            ) : isProcessing ? (
              <div className="flex flex-col items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mb-3"></div>
                <p className="text-purple-300 text-sm sm:text-base">
                  Processing your payment...
                </p>
              </div>
            ) : paymentMethod === "card" ? (
              <button
                onClick={handlePaymentSubmit}
                className="w-full py-3 rounded-md bg-gradient-to-r from-purple-600 to-fuchsia-500 hover:from-purple-700 hover:to-fuchsia-600 text-white font-medium transition-all duration-300 text-sm sm:text-base"
                disabled={isProcessing}
              >
                Pay ${plans[selectedPlan].price.toFixed(2)}
              </button>
            ) : (
              <div className="w-full rounded-md overflow-hidden">
                <div className="bg-[#0070ba]/20 backdrop-blur-sm p-3 rounded-md text-center mb-2 border border-blue-400/20">
                  <div className="flex items-center justify-center space-x-2">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 flex-shrink-0"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M7.984 19.496C9.55 20.608 11.291 21.248 13.016 21.248C14.742 21.248 16.483 20.608 18.049 19.496C19.614 18.384 20.656 17.168 21.096 15.968C21.536 14.768 21.536 13.328 21.096 11.936C20.656 10.544 20.096 9.136 19.08 7.808C18.066 6.48 16.699 5.328 15.049 4.352C13.398 3.376 11.744 3.152 9.953 3.008C8.162 2.864 6.368 2.72 4.792 3.584C3.213 4.448 2.112 5.808 1.392 7.312C0.672 8.816 0.336 10.448 0.336 11.936C0.336 13.424 0.672 14.608 1.392 15.968C2.112 17.328 3.213 18.384 4.792 19.496C6.368 20.608 7.984 21.248 9.953 21.248"
                        strokeWidth="2"
                        stroke="#60a5fa"
                        fill="none"
                      />
                    </svg>
                    <span className="text-blue-300 font-medium text-xs sm:text-sm">
                      Pay with PayPal: ${plans[selectedPlan].price.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Custom PayPal Button */}
                <button
                  onClick={handlePaymentSubmit}
                  className="w-full py-3 rounded-md bg-[#0070ba] hover:bg-[#003087] text-white font-medium transition-all duration-300 flex items-center justify-center text-sm sm:text-base"
                  disabled={isProcessing}
                >
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 mr-2"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 0 0-.794.68l-.04.22-.63 4.028-.02.11a.81.81 0 0 1-.8.68h-2.79a.403.403 0 0 1-.4-.34v-.06c.03-.18.06-.36.11-.53.45-2.28.9-4.58 1.34-6.87.02-.08.05-.18.09-.27a.459.459 0 0 1 .4-.23h1.2c1.3.07 2.57.02 3.82-.16.92-.13 1.76-.39 2.56-.79.96-.49 1.71-1.15 2.25-1.99.15-.23.28-.47.4-.72zm-6.598-5.826c.054 0 .109.001.164.003a.491.491 0 0 0 .05.01h3.26c.582 0 1.124.043 1.626.127a4.662 4.662 0 0 1 1.455.437c.473.227.877.532 1.212.916.334.384.6.84.798 1.367.198.533.294 1.116.294 1.75a3.755 3.755 0 0 1-.401 1.692c-.267.533-.644.994-1.131 1.382a4.82 4.82 0 0 1-1.631.892 8.242 8.242 0 0 1-1.933.392c-.616.075-1.605.091-2.536.091h-.343l-.059.001c-.277 0-.561 0-.85-.005a.461.461 0 0 0-.455.405l-.702 4.517h-2.729l1.841-10.767a.806.806 0 0 1 .795-.68h.269z" />
                  </svg>
                  Pay Now
                </button>

                {/* Hidden PayPal Button Fallback */}
                <div className="hidden">
                  <PayPalScriptProvider options={paypalOptions}>
                    <PayPalButtons
                      forceReRender={[selectedPlan, plans[selectedPlan].price]}
                      disabled={isProcessing}
                      style={{
                        layout: "horizontal",
                        color: "blue",
                        shape: "rect",
                        label: "pay",
                        height: 55,
                      }}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [
                            {
                              description: `Voodo Premium - ${plans[selectedPlan].name}`,
                              amount: {
                                currency_code: "USD",
                                value: plans[selectedPlan].price.toFixed(2),
                              },
                            },
                          ],
                        });
                      }}
                      onApprove={(data, actions) => {
                        return actions.order.capture().then((details) => {
                          // Handle the successful payment
                          const paymentData = {
                            planId: selectedPlan,
                            paymentMethod: "paypal",
                            paymentToken: details.id,
                            payerId: details.payer.payer_id,
                          };

                          // Process payment with our API
                          setIsProcessing(true);
                          premiumApi
                            .processPremium(paymentData)
                            .then((response) => {
                              if (response && response.success) {
                                // Store premium status in localStorage
                                const premiumData =
                                  premiumApi.storePremiumStatus({
                                    planId: selectedPlan,
                                    features: plans[selectedPlan].features,
                                  });

                                // Notify socket if connected
                                if (socket) {
                                  socket.emit("setPremiumStatus", {
                                    isPremium: true,
                                    expiryDate: premiumData.expiry,
                                  });
                                }

                                setIsPremiumActive(true);
                                setTimeout(() => {
                                  onClose();
                                  window.location.reload();
                                }, 1500);
                              } else {
                                setPaymentError(
                                  "Payment could not be processed"
                                );
                              }
                              setIsProcessing(false);
                            })
                            .catch((error) => {
                              console.error(
                                "PayPal payment processing error:",
                                error
                              );
                              setPaymentError(
                                "Payment failed. Please try again."
                              );
                              setIsProcessing(false);
                            });
                        });
                      }}
                      onError={(err) => {
                        console.error("PayPal error:", err);
                        setPaymentError(
                          "PayPal error: " + (err.message || "Unknown error")
                        );
                      }}
                    />
                  </PayPalScriptProvider>
                </div>
              </div>
            )}

            {/* Payment error message */}
            {paymentError && (
              <div className="mt-3 text-red-400 text-xs sm:text-sm text-center">
                <p>{paymentError}</p>
              </div>
            )}

            <p className="text-[10px] sm:text-xs text-gray-400 mt-3 sm:mt-4 text-center leading-relaxed">
              By purchasing, you agree to our Terms of Service.
              <br />
              Premium features are non-refundable.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PremiumModal;
